import { TENANT_NAV, type TenantNavCoveredPath, type TenantNavPath } from "./tenantNav";

const TENANT_PREFIX = "/tenants/$tenantId";

/** Pages under the tenant with no nav row of their own; the trail still needs their names. */
const CRUMB_ONLY = [
  { to: "/tenants/$tenantId/storefront/payments/revenue", label: "Revenue" },
  { to: "/tenants/$tenantId/openers/new", label: "New style" },
] as const satisfies readonly { to: TenantNavPath; label: string }[];

/** Tenant paths that render no trail: the legacy redirects, which never render at all. */
const UNCRUMBED = [
  "/tenants/$tenantId/catalog",
  "/tenants/$tenantId/gem-packs",
  "/tenants/$tenantId/balance-packs",
  "/tenants/$tenantId/payments",
  "/tenants/$tenantId/payments/revenue",
  "/tenants/$tenantId/domains",
  "/tenants/$tenantId/chat",
  "/tenants/$tenantId/storefront/branding",
  "/tenants/$tenantId/storefront/theme",
  "/tenants/$tenantId/storefront/rarity",
  "/tenants/$tenantId/storefront/terminology",
  "/tenants/$tenantId/storefront/shipping",
  "/tenants/$tenantId/storefront/cookie-consent",
  "/tenants/$tenantId/storefront/currency",
  "/tenants/$tenantId/storefront/features",
] as const satisfies readonly TenantNavPath[];

/**
 * A static tenant page the router knows that neither the nav, `CRUMB_ONLY` nor `UNCRUMBED`
 * names. `never` while every route is covered; `breadcrumbs.test.ts` asserts it, so a new tenant
 * route with no crumb fails typecheck instead of rendering "…".
 */
export type UncoveredTenantPath = Exclude<
  TenantNavPath,
  TenantNavCoveredPath | (typeof CRUMB_ONLY)[number]["to"] | (typeof UNCRUMBED)[number]
>;

interface TenantSection {
  label: string;
  to: TenantNavPath;
}

/**
 * Every crumb-bearing page keyed by its path below the tenant. A nested page only names its
 * own leaf: the trail walks prefixes, so storefront/battles picks "Storefront" up from the
 * shorter key on the way. The overview is skipped: the tenant itself is the whole trail there.
 */
function tenantSections(): Record<string, TenantSection> {
  const table: Record<string, TenantSection> = {};
  const add = (to: TenantNavPath, label: string) => {
    if (to === TENANT_PREFIX) return;
    table[to.slice(TENANT_PREFIX.length + 1)] = { label, to };
  };
  for (const group of TENANT_NAV) {
    for (const item of group.items) {
      add(item.to, item.label);
      for (const child of item.children ?? []) add(child.to, child.label);
    }
  }
  for (const page of CRUMB_ONLY) add(page.to, page.label);
  return table;
}

const TENANT_SECTIONS = tenantSections();

/** `path` is the crumb's slice of the URL — a stable identity for React keys. */
export interface Crumb {
  path: string;
  label: string;
  to?: TenantNavPath;
}

/**
 * The trail below the tenant for `pathname`, outermost first — empty on the tenant
 * overview, since the tenant itself is the whole trail there. An id segment
 * (boxes/$boxId) takes its label from `labels`, keyed by the id, and stays
 * unlinked: only a name we don't have yet reads as "…".
 */
export function tenantCrumbs(pathname: string, labels: Record<string, string> = {}): Crumb[] {
  const suffix = pathname.replace(/^\/tenants\/[^/]+/, "").replace(/^\/+|\/+$/g, "");
  if (!suffix) return [];

  return suffix.split("/").map((segment, i, segments) => {
    const path = segments.slice(0, i + 1).join("/");
    const section = TENANT_SECTIONS[path];
    return { path, ...(section ?? { label: labels[segment] ?? "…" }) };
  });
}
